import { useState, useRef } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const AnnotationsSummaryTab = ({
    annotations,
    summary,
    studies,
    loading,
    filters,
    onFilterChange,
    projectId,
    onRefresh,
    // Only project owners + admins can download. Editors and viewers can
    // browse annotations but no local save actions are rendered for them.
    canDownload = false,
}) => {
    const [expandedStudies, setExpandedStudies] = useState({});
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [downloadingAnnotation, setDownloadingAnnotation] = useState(null);
    const [downloadingMeasurements, setDownloadingMeasurements] = useState(null);
    const [deletingAnnotation, setDeletingAnnotation] = useState(null);
    const fileInputRef = useRef(null);

    // Group annotations by study
    const groupedByStudy = annotations.reduce((acc, ann) => {
        const studyId = ann.study_id;
        if (!acc[studyId]) {
            acc[studyId] = {
                studyName: ann.study_name,
                annotations: []
            };
        }
        acc[studyId].annotations.push(ann);
        return acc;
    }, {});

    const toggleStudy = (studyId) => {
        setExpandedStudies(prev => ({
            ...prev,
            [studyId]: !prev[studyId]
        }));
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Reset input so the same file can be re-selected
        e.target.value = '';

        try {
            setImporting(true);
            const formData = new FormData();
            formData.append('file', file);

            const response = await API.post(
                `/projects/${projectId}/annotations/import/`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            const { created, updated, errors } = response.data;
            toast.success(`Import complete: ${created} created, ${updated} updated.`);
            if (errors && errors.length > 0) {
                toast.warning(`${errors.length} error(s): ${errors.slice(0, 3).join('; ')}`);
            }
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error("Error importing annotations:", err);
            toast.error(err.response?.data?.error || "Failed to import annotations.");
        } finally {
            setImporting(false);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams();
            if (filters.studyId) params.append('study_id', filters.studyId);

            const response = await API.get(
                `/projects/${projectId}/annotations/export/?${params.toString()}`,
                { responseType: 'blob' }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `annotations_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Annotations exported successfully!");
        } catch (err) {
            console.error("Error exporting annotations:", err);
            toast.error("Failed to export annotations.");
        } finally {
            setExporting(false);
        }
    };

    const handleDownloadSegmentation = async (annotationId, format) => {
        try {
            setDownloadingAnnotation(annotationId);

            const response = await API.get(
                `/projects/${projectId}/download-segmentation/?annotation_id=${annotationId}&format=${format}`,
                { responseType: 'blob' }
            );

            // Get filename from Content-Disposition header or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `segmentation_${annotationId}.${format === 'nifti' ? 'nii.gz' : 'zip'}`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`Segmentation downloaded as ${format.toUpperCase()}!`);
        } catch (err) {
            console.error("Error downloading segmentation:", err);
            // The request uses responseType: 'blob' so an error response
            // body arrives as a Blob rather than the parsed JSON axios
            // usually gives us. Read the blob as text and pull out the
            // backend's `error` field so the toast shows the real reason
            // (e.g., "Segmentation file not found on disk", "Failed to
            // create trame download: ...") instead of a generic message.
            let detail = '';
            const errBlob = err?.response?.data;
            if (errBlob instanceof Blob) {
                try {
                    const text = await errBlob.text();
                    try {
                        const parsed = JSON.parse(text);
                        detail = parsed?.error || text.slice(0, 200);
                    } catch {
                        detail = text.slice(0, 200);
                    }
                } catch { /* keep detail empty */ }
            } else if (typeof errBlob === 'object' && errBlob?.error) {
                detail = errBlob.error;
            } else if (err?.message) {
                detail = err.message;
            }
            toast.error(detail ? `Download failed: ${detail}` : "Failed to download segmentation.");
        } finally {
            setDownloadingAnnotation(null);
        }
    };

    const handleDownloadMeasurements = async (ann) => {
        try {
            setDownloadingMeasurements(ann.id);

            // Trame-slicer markups: fetch the JSON directly from the project
            // download-segmentation endpoint (which also handles trame_markup_ ids).
            let response;
            if (ann.annotation_source === 'trame_slicer_markup') {
                response = await API.get(
                    `/projects/${projectId}/download-segmentation/?annotation_id=${encodeURIComponent(ann.id)}`,
                    { responseType: 'blob' }
                );
            } else {
                if (!ann.study_id || !ann.patient_id || !ann.annotator_id) return;
                const params = new URLSearchParams();
                if (ann.data_type) params.append('data_type', ann.data_type);
                if (ann.series_id) params.append('series_id', ann.series_id);
                if (ann.annotator_id) params.append('annotator_id', ann.annotator_id);
                params.append('download', '1');

                response = await API.get(
                    `/studies/${ann.study_id}/ohif-measurements/${encodeURIComponent(ann.patient_id)}/?${params.toString()}`,
                    { responseType: 'blob' }
                );
            }

            const cd = response.headers['content-disposition'];
            let filename = `measurements_study${ann.study_id}_${ann.patient_id}.json`;
            if (cd) {
                const m = cd.match(/filename="?([^";\n]+)"?/);
                if (m) filename = m[1];
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Measurements downloaded.");
        } catch (err) {
            console.error("Error downloading measurements:", err);
            toast.error("Failed to download measurements.");
        } finally {
            setDownloadingMeasurements(null);
        }
    };

    const handleDeleteAnnotation = async (ann) => {
        const label = `${ann.patient_id} (${ann.study_name})`;
        if (!window.confirm(`Delete annotation for ${label}? This cannot be undone.`)) {
            return;
        }
        try {
            setDeletingAnnotation(ann.id);
            await API.delete(`/projects/${projectId}/annotations/${ann.id}/`);
            toast.success("Annotation deleted.");
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error("Error deleting annotation:", err);
            toast.error(err.response?.data?.error || "Failed to delete annotation.");
        } finally {
            setDeletingAnnotation(null);
        }
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'lead': return 'bg-danger';
            case 'contributor': return 'bg-info';
            case 'reviewer': return 'bg-warning text-dark';
            case 'ai': return 'bg-purple';  // Special style for AI-generated
            default: return 'bg-secondary';
        }
    };

    // Check if annotation is a segmentation (either manual OHIF or AI model)
    const isSegmentation = (ann) => ann.annotation_type === 'segmentation';

    // Check if it's an AI model segmentation
    const isAIModelSegmentation = (ann) => ann.annotation_source === 'ai_model';

    // Check if it's a manual OHIF segmentation
    const isManualOHIFSegmentation = (ann) => ann.annotation_source === 'manual_ohif';

    // Check if it's a trame-slicer 3D segmentation
    const isTrameSegmentation = (ann) => ann.annotation_source === 'trame_slicer';

    // Check if it's a trame-slicer markup / measurement
    const isTrameMarkup = (ann) => ann.annotation_source === 'trame_slicer_markup';

    const getViewerBadge = (ann) => {
        if (isTrameSegmentation(ann) || isTrameMarkup(ann)) return <span className="badge text-white" style={{backgroundColor: '#0284c7'}}>3D Slicer</span>;
        if (isManualOHIFSegmentation(ann)) return <span className="badge text-white" style={{backgroundColor: '#7c3aed'}}>OHIF</span>;
        if (isAIModelSegmentation(ann)) return <span className="badge bg-secondary">AI Model</span>;
        return <span className="text-muted">-</span>;
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading annotations...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Annotation Summary</h5>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing}
                    >
                        {importing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1"></span>
                                Importing...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-upload me-1"></i>
                                Import CSV
                            </>
                        )}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        style={{ display: 'none' }}
                        onChange={handleImport}
                    />
                    {canDownload && (
                        <button
                            className="btn btn-success btn-sm"
                            onClick={handleExport}
                            disabled={annotations.length === 0 || exporting}
                        >
                            {exporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-download me-1"></i>
                                    Export CSV
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            <div className="card-body">
                {/* Summary Stats */}
                {summary && (
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.total_annotations}</h4>
                                <small className="text-muted">Total Annotations</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.unique_patients}</h4>
                                <small className="text-muted">Unique Patients</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.unique_annotators}</h4>
                                <small className="text-muted">Annotators</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.studies_with_annotations}</h4>
                                <small className="text-muted">Studies with Data</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <label className="form-label small text-muted">Filter by Study</label>
                        <select
                            className="form-select"
                            value={filters.studyId}
                            onChange={(e) => onFilterChange({ ...filters, studyId: e.target.value })}
                        >
                            <option value="">All Studies</option>
                            {studies.map(study => (
                                <option key={study.id} value={study.id}>{study.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Annotations Table */}
                {annotations.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fa fa-clipboard mb-3" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
                        <p>No annotations found.</p>
                        <small>Annotations will appear here once team members annotate patient data in study viewers.</small>
                    </div>
                ) : (
                    <div className="accordion" id="annotationsAccordion">
                        {Object.entries(groupedByStudy).map(([studyId, data]) => (
                            <div className="accordion-item" key={studyId}>
                                <h2 className="accordion-header">
                                    <button
                                        className={`accordion-button ${expandedStudies[studyId] ? '' : 'collapsed'}`}
                                        type="button"
                                        onClick={() => toggleStudy(studyId)}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <i className={`fa fa-chevron-${expandedStudies[studyId] ? 'down' : 'right'}`}></i>
                                            <span className="fw-bold">{data.studyName}</span>
                                            <span className="badge bg-secondary">
                                                {data.annotations.length} annotation(s)
                                            </span>
                                        </div>
                                    </button>
                                </h2>
                                <div className={`accordion-collapse collapse ${expandedStudies[studyId] ? 'show' : ''}`}>
                                    <div className="accordion-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Patient ID</th>
                                                        <th>Series</th>
                                                        <th>Viewer</th>
                                                        <th>Annotator</th>
                                                        <th>Role</th>
                                                        <th>Labels</th>
                                                        <th>Signal Regions</th>
                                                        <th>Measurements</th>
                                                        <th>Questions</th>
                                                        <th>Scores</th>
                                                        <th>Time Spent</th>
                                                        <th>Complete</th>
                                                        <th>Updated</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.annotations.map(ann => (
                                                        <tr key={ann.id} className={isSegmentation(ann) ? (isAIModelSegmentation(ann) ? 'table-info' : 'table-success') : ''}>
                                                            <td>
                                                                <code>{ann.patient_id}</code>
                                                            </td>
                                                            <td>
                                                                {ann.series_id ? (
                                                                    <span
                                                                        className="text-nowrap"
                                                                        title={ann.description || ann.data_type || ''}
                                                                        style={{fontSize: '0.85rem'}}
                                                                    >
                                                                        {ann.series_id}
                                                                    </span>
                                                                ) : ann.annotation_level === 'data_type' ? (
                                                                    <span className="badge bg-info text-dark" title={ann.data_type}>
                                                                        {ann.data_type || 'Data Type'}
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-secondary" title="Patient-level annotation">
                                                                        Patient
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>{getViewerBadge(ann)}</td>
                                                            <td>
                                                                {isSegmentation(ann) ? (
                                                                    isAIModelSegmentation(ann) ? (
                                                                        <span className="text-muted">
                                                                            <i className="fa fa-brain me-1"></i>
                                                                            {ann.annotator_username}
                                                                        </span>
                                                                    ) : (
                                                                        <span>
                                                                            <i className="fa fa-user me-1 text-success"></i>
                                                                            {ann.annotator_username}
                                                                        </span>
                                                                    )
                                                                ) : (
                                                                    ann.annotator_username
                                                                )}
                                                            </td>
                                                            <td>
                                                                {isAIModelSegmentation(ann) ? (
                                                                    <span className="badge" style={{backgroundColor: '#9333ea'}}>
                                                                        AI Model
                                                                    </span>
                                                                ) : isManualOHIFSegmentation(ann) ? (
                                                                    <span className="badge" style={{backgroundColor: '#059669'}}>
                                                                        Manual
                                                                    </span>
                                                                ) : (
                                                                    <span className={`badge ${getRoleBadgeClass(ann.annotator_role)}`}>
                                                                        {ann.annotator_role}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {ann.labels_display && ann.labels_display.length > 0 ? (
                                                                    ann.labels_display.map((label, idx) => (
                                                                        <span key={idx} className={`badge ${label.startsWith('[Signal]') ? 'bg-info text-dark' : isSegmentation(ann) ? 'bg-success' : 'bg-primary'} me-1`}>
                                                                            {label.startsWith('[Signal] ') ? label.slice(9) : label}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {ann.signal_regions_summary ? (
                                                                    <span className="badge bg-info text-dark" title={
                                                                        Object.entries(ann.signal_regions_summary.labels || {})
                                                                            .map(([name, count]) => `${name}: ${count}`)
                                                                            .join(', ')
                                                                    }>
                                                                        <i className="fa fa-wave-square me-1"></i>
                                                                        {ann.signal_regions_summary.total_regions} region{ann.signal_regions_summary.total_regions !== 1 ? 's' : ''}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {ann.measurements_summary ? (
                                                                    <span className="badge bg-primary" title={
                                                                        Object.entries(ann.measurements_summary.tools || {})
                                                                            .map(([name, count]) => `${name}: ${count}`)
                                                                            .join(', ')
                                                                    }>
                                                                        <i className="fa fa-ruler me-1"></i>
                                                                        {ann.measurements_summary.count}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {isSegmentation(ann) ? (
                                                                    <span className="text-muted">-</span>
                                                                ) : (() => {
                                                                    // Source of truth = the answer dict on this annotation. We then
                                                                    // look up question prompt text from the study (if `studies` carries
                                                                    // it — see DVStudyListSerializer); otherwise we fall back to a
                                                                    // generic "Question N" label so answers still render even when the
                                                                    // study prop is light on data. Accept both canonical `question`
                                                                    // and legacy `text` field shapes (matches ViewerNavbar.jsx).
                                                                    const study = studies.find(s => s.id === ann.study_id);
                                                                    const qs = (study?.questions) || [];
                                                                    const qTextById = new Map(
                                                                        qs.map((q, i) => [String(q.id), { text: q.question || q.text || `Question ${i + 1}`, idx: i }])
                                                                    );
                                                                    const cleanAnswers = Object.fromEntries(
                                                                        Object.entries(ann.question_answers || {}).filter(([k]) => !k.startsWith('_'))
                                                                    );
                                                                    const total = qs.length || ann.questions_summary?.total || Object.keys(cleanAnswers).length;
                                                                    if (total === 0) return <span className="text-muted">-</span>;

                                                                    const items = Object.entries(cleanAnswers).map(([key, ans], fallbackIdx) => {
                                                                        if (ans === undefined || ans === null || ans === '') return null;
                                                                        if (Array.isArray(ans) && ans.length === 0) return null;
                                                                        const meta = qTextById.get(key);
                                                                        const text = meta?.text || `Question ${fallbackIdx + 1}`;
                                                                        const idx = meta?.idx ?? fallbackIdx;
                                                                        const ansDisplay = Array.isArray(ans) ? ans.join(', ') : String(ans);
                                                                        return { key, idx, text, ansDisplay };
                                                                    }).filter(Boolean);
                                                                    items.sort((a, b) => a.idx - b.idx);
                                                                    const answeredCount = items.length;

                                                                    const tooltip = items
                                                                        .map(i => `Q${i.idx + 1} — ${i.text}: ${i.ansDisplay}`)
                                                                        .join('\n');

                                                                    return (
                                                                        <div className="d-flex flex-column align-items-start gap-1" title={tooltip}>
                                                                            <span className={`badge ${answeredCount === total ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                                                {answeredCount}/{total}
                                                                            </span>
                                                                            {items.map(i => (
                                                                                <small
                                                                                    key={i.key}
                                                                                    className="text-muted text-truncate"
                                                                                    style={{ maxWidth: '220px' }}
                                                                                >
                                                                                    <strong>Q{i.idx + 1}:</strong> {i.ansDisplay}
                                                                                </small>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td>
                                                                {isSegmentation(ann) ? (
                                                                    <span className="text-muted">-</span>
                                                                ) : (() => {
                                                                    // Same pattern as Questions: iterate `ann.score_answers` and look
                                                                    // up label text from `study.scores` when available.
                                                                    const study = studies.find(s => s.id === ann.study_id);
                                                                    const sc = (study?.scores) || [];
                                                                    const sTextById = new Map(
                                                                        sc.map((s, i) => [String(s.id), { text: s.name || s.label || s.question || `Score ${i + 1}`, idx: i }])
                                                                    );
                                                                    const sAnswers = ann.score_answers || {};
                                                                    const total = sc.length || ann.scores_summary?.total || Object.keys(sAnswers).length;
                                                                    if (total === 0) return <span className="text-muted">-</span>;

                                                                    const items = Object.entries(sAnswers).map(([key, ans], fallbackIdx) => {
                                                                        if (ans === undefined || ans === null || ans === '') return null;
                                                                        const meta = sTextById.get(key);
                                                                        const text = meta?.text || `Score ${fallbackIdx + 1}`;
                                                                        const idx = meta?.idx ?? fallbackIdx;
                                                                        return { key, idx, text, ansDisplay: String(ans) };
                                                                    }).filter(Boolean);
                                                                    items.sort((a, b) => a.idx - b.idx);
                                                                    const answeredCount = items.length;

                                                                    const tooltip = items
                                                                        .map(i => `${i.text}: ${i.ansDisplay}`)
                                                                        .join('\n');

                                                                    return (
                                                                        <div className="d-flex flex-column align-items-start gap-1" title={tooltip}>
                                                                            <span className={`badge ${answeredCount === total ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                                                {answeredCount}/{total}
                                                                            </span>
                                                                            {items.map(i => (
                                                                                <small
                                                                                    key={i.key}
                                                                                    className="text-muted text-truncate"
                                                                                    style={{ maxWidth: '160px' }}
                                                                                >
                                                                                    <strong>{i.text}:</strong> {i.ansDisplay}
                                                                                </small>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td>
                                                                {ann.time_spent_display ? (
                                                                    <span className="badge bg-info text-dark">
                                                                        <i className="fa fa-clock me-1"></i>
                                                                        {ann.time_spent_display}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {ann.is_complete ? (
                                                                    <i className="fa fa-check-circle text-success"></i>
                                                                ) : (
                                                                    <i className="fa fa-times-circle text-danger"></i>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <small className="text-muted">
                                                                    {ann.updated_at ? new Date(ann.updated_at).toLocaleDateString() : '-'}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                <div className="btn-group btn-group-sm">
                                                                    {canDownload && ann.measurements_summary && ann.measurements_summary.count > 0 && (
                                                                        <button
                                                                            className="btn btn-outline-info btn-sm"
                                                                            onClick={() => handleDownloadMeasurements(ann)}
                                                                            disabled={downloadingMeasurements === ann.id}
                                                                            title="Download measurements as JSON"
                                                                        >
                                                                            {downloadingMeasurements === ann.id ? (
                                                                                <span className="spinner-border spinner-border-sm"></span>
                                                                            ) : (
                                                                                <>
                                                                                    <i className="fa fa-ruler me-1"></i>
                                                                                    JSON
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                    {canDownload && isSegmentation(ann) && (
                                                                        <>
                                                                            {!isTrameSegmentation(ann) && (
                                                                                <button
                                                                                    className="btn btn-outline-primary btn-sm"
                                                                                    onClick={() => handleDownloadSegmentation(ann.id, 'dicom')}
                                                                                    disabled={downloadingAnnotation === ann.id}
                                                                                    title="Download as DICOM SEG"
                                                                                >
                                                                                    {downloadingAnnotation === ann.id ? (
                                                                                        <span className="spinner-border spinner-border-sm"></span>
                                                                                    ) : (
                                                                                        <>
                                                                                            <i className="fa fa-download me-1"></i>
                                                                                            DICOM
                                                                                        </>
                                                                                    )}
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                className="btn btn-outline-success btn-sm"
                                                                                onClick={() => handleDownloadSegmentation(ann.id, 'nifti')}
                                                                                disabled={downloadingAnnotation === ann.id}
                                                                                title="Download as NIfTI zip"
                                                                            >
                                                                                {downloadingAnnotation === ann.id ? (
                                                                                    <span className="spinner-border spinner-border-sm"></span>
                                                                                ) : (
                                                                                    <>
                                                                                        <i className="fa fa-download me-1"></i>
                                                                                        NIfTI
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => handleDeleteAnnotation(ann)}
                                                                        disabled={deletingAnnotation === ann.id}
                                                                        title="Delete annotation"
                                                                    >
                                                                        {deletingAnnotation === ann.id ? (
                                                                            <span className="spinner-border spinner-border-sm"></span>
                                                                        ) : (
                                                                            <i className="fa fa-trash"></i>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnnotationsSummaryTab;
